import { useEffect, useRef, useState } from 'react';
import {
  Stack,
  Group,
  Text,
  Title,
  Paper,
  Button,
  ThemeIcon,
  SimpleGrid,
  Skeleton,
  AspectRatio,
  Box,
  ActionIcon,
  Badge,
} from '@mantine/core';
import { RecipeImage } from '../../UI/RecipeImage';
import { RecipeSelectionDrawer } from '../RecipeSelectionDrawer';
import { TopPickBadge, TrendingBadge, recipeMark } from '../../UI/TopPickRibbon';
import { NutritionDrawer } from '../../Recipe/NutritionDrawer';
import {
  IconArrowLeft,
  IconArrowRight,
  IconListCheck,
  IconCheck,
  IconClock,
  IconChartBar,
  IconHeart,
  IconBookmark,
  IconSparkles,
  IconWand,
  IconBook,
  IconRefresh,
  IconLeaf,
  IconChefHat,
  IconBulb,
} from '@tabler/icons-react';
import { useMediaQuery } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { usePostHog } from '@posthog/react';
import { Recipe } from '../../../types/mealPlan';
import { useAuth } from '../../../hooks/useAuth';
import { AnalyticsEvent } from '../../../utils/analytics';
import { formatTime } from '../../../utils/formatTime';
import { HouseholdSegment, bySegment } from '../../../utils/householdSegment';
import { getMeatlessLabel } from '../../../utils/recipeBadges';
import { isRecipeSummary } from '../../../utils/recipeSummary';
import { fetchRecipeList, getCachedRecipeList } from '../../../utils/recipeListCache';
import {
  PickerCandidatesResponse,
  PickerCandidatesCache,
  buildPickerRequest,
  fetchPickerCandidates,
  requestPickerCandidates,
} from '../../../utils/pickerCandidates';

export type PickerValue = {
  lockedRecipeIds: string[]; // cookbook recipe picks
  lockedCatalogRecipeIds: string[]; // "New for you" catalog picks
};

// The request shape and its cache key live in utils/pickerCandidates so the
// wizard can warm this step's shortlist before it mounts; re-exported here
// because this step is still where they're consumed from.
export type { PickerCandidatesResponse, PickerCandidatesCache };

type PickerStepProps = {
  value: PickerValue;
  onChange: (value: PickerValue) => void;
  onNext: () => void;
  onBack: () => void;
  dateRange: [string | null, string | null];
  uniqueMeals?: number;
  // Nights already claimed by penciled-in meals; they don't need a recipe, so
  // they come off the pick budget and the "we'll choose N" copy
  penciledCount?: number;
  // "Tell us how you like to meal prep" free text — tints the server's
  // meal-prep slice retrieval when meal prep is on
  mealPrepPreferences?: string;
  candidatesCache: PickerCandidatesCache | null;
  onCandidatesLoaded: (cache: PickerCandidatesCache) => void;
  // Claims the one "shortlist shown" impression for a cache key, returning
  // false if it has already been claimed. Wizard-held rather than local: the
  // shortlist may have been fetched by the wizard's prefetch instead of by
  // this step, and this step unmounts on every back/forward within the wizard.
  claimShortlistShown: (cacheKey: string) => boolean;
  userDietaryRestrictions: string[];
  householdSegment: HouseholdSegment;
};

type PickerSection = {
  key: 'favorites' | 'cookbook' | 'newForYou';
  title: string;
  icon: React.ReactNode;
  recipes: Recipe[];
  isCatalog: boolean;
  headerAction?: React.ReactNode;
  // Repeat of the header action, below the grid. "New for you" runs to fifteen
  // cards — eight rows on a phone — and a control that only exists above them
  // is off screen for anyone who has actually read the section.
  footerAction?: React.ReactNode;
  subtitle?: React.ReactNode;
  notice?: React.ReactNode;
};

export const PickerStep = ({
  value,
  onChange,
  onNext,
  onBack,
  dateRange,
  uniqueMeals,
  penciledCount,
  mealPrepPreferences,
  candidatesCache,
  onCandidatesLoaded,
  claimShortlistShown,
  userDietaryRestrictions,
  householdSegment,
}: PickerStepProps) => {
  const { user } = useAuth();
  const posthog = usePostHog();
  // Pointer-adaptive copy: "tap" for touch devices, "click" for mouse/trackpad
  const isCoarsePointer = useMediaQuery('(pointer: coarse)');
  // Built from the same helper the wizard's prefetch uses, so a warmed
  // shortlist lands on the key this step looks up
  const request = buildPickerRequest({ dateRange, uniqueMeals, mealPrepPreferences });
  const { cacheKey, totalDays, mealPrepEnabled } = request;
  const cachedData = candidatesCache?.key === cacheKey ? candidatesCache.data : null;
  const [candidates, setCandidates] = useState<PickerCandidatesResponse | null>(cachedData);
  const [loading, setLoading] = useState(cachedData === null);
  const [loadError, setLoadError] = useState(false);
  // Two-state drawer pattern (same as RecipeCard): the drawer must mount
  // closed and open on the next tick for the slide-in to animate, and stay
  // mounted while closing so the slide-out can run
  const [nutritionRecipe, setNutritionRecipe] = useState<{
    recipe: Recipe;
    isCatalog: boolean;
  } | null>(null);
  const [nutritionDrawerOpened, setNutritionDrawerOpened] = useState(false);
  // Browse picks come from the cookbook list, whose summaries carry only
  // nutrition.perServing; the drawer needs the full recipe (breakdown,
  // ingredient count) or it would regenerate a breakdown that exists.
  const fullRecipesRef = useRef<Map<string, Recipe>>(new Map());

  const openNutrition = async (recipe: Recipe, isCatalog: boolean) => {
    let shown = fullRecipesRef.current.get(recipe.id) ?? recipe;
    if (!isCatalog && user && isRecipeSummary(shown)) {
      try {
        const token = await user.getIdToken();
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/u/${user.uid}/recipes/${recipe.id}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (response.ok) {
          const data = await response.json();
          if (data.recipe) {
            fullRecipesRef.current.set(recipe.id, data.recipe);
            shown = data.recipe;
          }
        }
      } catch (error) {
        console.error('Failed to load recipe nutrition:', error);
      }
    }
    setNutritionRecipe({ recipe: shown, isCatalog });
    setTimeout(() => setNutritionDrawerOpened(true), 0);
  };
  // "Browse my cookbook": full-cookbook multi-select for picking specific
  // saved recipes the shortlist didn't surface. Fetched lazily on first open.
  const [browseOpened, setBrowseOpened] = useState(false);
  // Seeded from the session's shared cookbook list so it opens instantly
  // when the meal plan drawer (or an earlier browse) already loaded it
  const [fullCookbook, setFullCookbook] = useState<Recipe[] | null>(
    () => getCachedRecipeList('cookbook', user?.uid) ?? null
  );
  const [cookbookLoading, setCookbookLoading] = useState(false);
  // "Spin again": re-rolls the New for you section (picked cards stay pinned)
  const [refreshing, setRefreshing] = useState(false);
  const [refreshCount, setRefreshCount] = useState(0);
  // Every catalog id shown this session. Spins exclude the whole set, not
  // just the cards on screen, so repeated spins keep moving into fresh
  // territory instead of ping-ponging between two rolls.
  const seenNewForYouIds = useRef<Set<string>>(new Set());
  useEffect(() => {
    for (const recipe of candidates?.newForYou ?? []) {
      seenNewForYouIds.current.add(recipe.id);
    }
  }, [candidates]);

  // Penciled nights come off the budget: only cooking days need recipes.
  // Mirrors derivePlanPreferences' clamp (a uniqueMeals set before nights
  // were penciled may exceed the remaining cooking days). The fully-penciled
  // case never reaches this step — the wizard collapses it away.
  const cookingDays = Math.max(1, totalDays - (penciledCount ?? 0));
  const mealsNeeded =
    uniqueMeals !== undefined ? Math.min(uniqueMeals, cookingDays) : cookingDays;
  const pickedCount = value.lockedRecipeIds.length + value.lockedCatalogRecipeIds.length;
  const atCapacity = pickedCount >= mealsNeeded;

  // Source composition of a freshly served shortlist (initial load or spin —
  // measured on the server response, not the pinned-card merge, so the metric
  // reflects what retrieval actually served).
  const captureShortlistShown = (
    data: PickerCandidatesResponse,
    trigger: 'initial' | 'spin'
  ) => {
    const sources = data.newForYouSources ?? {};
    const counts: Record<string, number> = {};
    for (const recipe of data.newForYou) {
      const source = sources[recipe.id] ?? 'unknown';
      counts[source] = (counts[source] ?? 0) + 1;
    }
    const total = data.newForYou.length;
    posthog?.capture(AnalyticsEvent.PickerShortlistShown, {
      trigger,
      new_for_you_count: total,
      source_base: counts.base ?? 0,
      source_exploration: counts.exploration ?? 0,
      source_prep: counts.prep ?? 0,
      source_trending: counts.trending ?? 0,
      source_backfill: counts.backfill ?? 0,
      source_unknown: counts.unknown ?? 0,
      exploration_share: total > 0 ? (counts.exploration ?? 0) / total : 0,
    });
  };

  useEffect(() => {
    // Cache hit (remount, unchanged week, or the wizard's prefetch landed
    // before this step opened): nothing to fetch
    if (candidatesCache?.key === cacheKey) {
      setCandidates(candidatesCache.data);
      setLoading(false);
      return;
    }
    let cancelled = false;
    const loadCandidates = async () => {
      if (!user) return;
      setLoading(true);
      setLoadError(false);
      try {
        // Joins the wizard's warm-up request when one is still in flight for
        // this key, rather than opening a second identical one
        const data = await fetchPickerCandidates(user, request);
        if (!cancelled) {
          setCandidates(data);
          onCandidatesLoaded({ key: cacheKey, data });
        }
      } catch (error) {
        console.error('Failed to load picker candidates:', error);
        if (!cancelled) setLoadError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    loadCandidates();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, cacheKey]);

  // Impression analytics fire when the shortlist is actually on screen, not
  // when it was fetched — the wizard may have warmed it a step earlier, and a
  // warmed shortlist the user never reaches was never shown. The claim is
  // wizard-scoped, so back/forward within the wizard doesn't re-report the
  // same shortlist and a fresh wizard session still reports its own.
  useEffect(() => {
    if (!candidates) return;
    if (!claimShortlistShown(cacheKey)) return;
    captureShortlistShown(candidates, 'initial');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [candidates, cacheKey]);

  const handleOpenBrowse = async () => {
    setBrowseOpened(true);
    if (!user) return;
    // Revalidate on every open; skeletons only when there's no list yet
    const hasList = fullCookbook !== null;
    if (!hasList) setCookbookLoading(true);
    try {
      setFullCookbook(await fetchRecipeList(user, 'cookbook'));
    } catch (error) {
      console.error('Failed to load cookbook:', error);
      // Keep a list already on screen; otherwise show the empty state
      if (!hasList) setFullCookbook([]);
    } finally {
      setCookbookLoading(false);
    }
  };

  const toggleBrowsePick = (recipe: Recipe) => {
    const picked = value.lockedRecipeIds.includes(recipe.id);
    if (!picked && atCapacity) {
      notifications.show({
        message: `You've already picked ${mealsNeeded} meals. Unpick one to swap this in.`,
        color: 'orange',
        autoClose: 3000,
      });
      return;
    }
    onChange({
      ...value,
      lockedRecipeIds: picked
        ? value.lockedRecipeIds.filter((id) => id !== recipe.id)
        : [...value.lockedRecipeIds, recipe.id],
    });
    posthog?.capture(
      picked ? AnalyticsEvent.PickerRecipeUnlocked : AnalyticsEvent.PickerRecipeLocked,
      {
        section: 'cookbook_browse',
        recipe_id: recipe.id,
        picked_count: pickedCount + (picked ? -1 : 1),
      }
    );
  };

  // "Spin the wheel" on New for you: refetch with the current cards excluded
  // so the roll comes back fresh. Picked cards hold their slots; only the
  // catalog section changes (favorites/cookbook stay put — the cookbook is
  // shuffled server-side, and reshuffling it as a side effect would be chaos).
  // `location` distinguishes the header control from the one below the grid,
  // so the split tells us whether the footer is what users actually reach.
  const handleSpinAgain = async (location: 'header' | 'footer') => {
    if (!user || !candidates || refreshing) return;
    setRefreshing(true);
    try {
      // Most recent 100: the server caps the list, and if it has to trim we
      // want it forgetting the oldest rolls, not the newest. Not routed
      // through the cache-key dedupe — a spin is a deliberate refetch of the
      // same key, which is exactly what that map exists to collapse.
      const seenIds = Array.from(seenNewForYouIds.current).slice(-100);
      const data = await requestPickerCandidates(user, request, seenIds);
      const pickedIds = new Set(value.lockedCatalogRecipeIds);
      const freshCards = data.newForYou.filter((r) => !pickedIds.has(r.id));
      // Positional merge: picked cards hold their exact grid slots so a spin
      // never visually relocates them; fresh cards fill the open slots in
      // server order. If the catalog comes back short, unfilled slots
      // collapse (picked cards can then shift up, but never reorder).
      const freshQueue = [...freshCards];
      const newForYou = candidates.newForYou
        .map((current) =>
          pickedIds.has(current.id) ? current : freshQueue.shift()
        )
        .filter((r): r is Recipe => !!r);
      const merged: PickerCandidatesResponse = {
        favorites: candidates.favorites,
        cookbook: candidates.cookbook,
        newForYou,
        // Carried forward: the merge keeps the original cookbook sections, and
        // dropping these would resurrect "Browse all"/"Fresh ideas" that the
        // first response already ruled out
        cookbookTotal: candidates.cookbookTotal ?? data.cookbookTotal,
        newForYouTarget: candidates.newForYouTarget ?? data.newForYouTarget,
        // Unioned, not carried forward: the merged section holds cards from
        // both responses, so either roll's trending marks still apply. IDs not
        // on screen are simply never looked up.
        trendingCatalogRecipeIds: [
          ...(candidates.trendingCatalogRecipeIds ?? []),
          ...(data.trendingCatalogRecipeIds ?? []),
        ],
        // Unioned like trending marks: the merged section holds cards from
        // both responses, and lock events look sources up by card id
        newForYouSources: {
          ...(candidates.newForYouSources ?? {}),
          ...(data.newForYouSources ?? {}),
        },
      };
      setCandidates(merged);
      onCandidatesLoaded({ key: cacheKey, data: merged });
      captureShortlistShown(data, 'spin');
      const nextCount = refreshCount + 1;
      setRefreshCount(nextCount);
      posthog?.capture(AnalyticsEvent.PickerRefreshed, {
        refresh_count: nextCount,
        picked_count: pickedCount,
        fresh_count: freshCards.length,
        location,
      });
    } catch (error) {
      console.error('Failed to refresh picker candidates:', error);
      notifications.show({
        message: "Couldn't grab fresh ideas right now. Try again in a moment.",
        color: 'orange',
        autoClose: 3000,
      });
    } finally {
      setRefreshing(false);
    }
  };

  const trendingCatalogIds = new Set(candidates?.trendingCatalogRecipeIds ?? []);

  // Picks made via browse that aren't in the shortlist still need a card
  const visibleIds = new Set(
    candidates
      ? [...candidates.favorites, ...candidates.cookbook, ...candidates.newForYou].map((r) => r.id)
      : []
  );
  const browseExtras = (fullCookbook ?? []).filter(
    (r) => value.lockedRecipeIds.includes(r.id) && !visibleIds.has(r.id)
  );

  // Distinct cookbook recipes already on screen (favorites + cookbook sections,
  // plus any browse picks pulled in as extras). "New for you" is catalog, not
  // cookbook, so it's excluded. If these cover the whole saved cookbook there's
  // nothing left for Browse all to reveal, so the action is hidden. Falls back
  // to showing it when the server didn't report a total (older cached data).
  const visibleCookbookCount = candidates
    ? new Set([
        ...candidates.favorites.map((r) => r.id),
        ...candidates.cookbook.map((r) => r.id),
        ...browseExtras.map((r) => r.id),
      ]).size
    : 0;
  const hasMoreCookbook =
    candidates?.cookbookTotal === undefined ||
    visibleCookbookCount < candidates.cookbookTotal;

  // "Fresh ideas" re-rolls the catalog section. A section that came back short
  // of the budget the server sized it for means the filtered catalog is
  // already drained for this user — spinning would hand back the same cards
  // (or fewer), so the action is hidden. Falls back to showing it when the
  // server didn't report a target (older cached data).
  const hasMoreNewForYou =
    candidates?.newForYouTarget === undefined ||
    candidates.newForYou.length >= candidates.newForYouTarget;

  // Repeated spins read as "I have to fill every slot and none of these fit".
  // Two spins in, or one spin that used up the catalog, the section stops
  // being the answer: the user needs to know the slots fill themselves, that
  // the next step takes requests, and that nothing here is final. The
  // retired-button case waits for a spin on purpose so the nudge lands as an
  // explanation of where the action went, not as an unprompted apology on a
  // section the user hasn't touched.
  const showNudge = refreshCount >= 2 || (refreshCount >= 1 && !hasMoreNewForYou);

  const nudgeShown = useRef(false);
  useEffect(() => {
    if (!showNudge || nudgeShown.current) return;
    nudgeShown.current = true;
    posthog?.capture(AnalyticsEvent.PickerNudgeShown, {
      refresh_count: refreshCount,
      picked_count: pickedCount,
      meals_needed: mealsNeeded,
      catalog_drained: !hasMoreNewForYou,
    });
  }, [showNudge, refreshCount, pickedCount, mealsNeeded, hasMoreNewForYou, posthog]);

  // The nudge's exit: requests on the next step are what actually get new
  // recipes built, so hand the user straight there rather than describing it
  const handleNudgeContinue = () => {
    posthog?.capture(AnalyticsEvent.PickerNudgeEngaged, {
      refresh_count: refreshCount,
      picked_count: pickedCount,
    });
    onNext();
  };

  // Lives beside the button being mashed, and still names all three ways out
  // (the week fills itself, the next step takes requests, nothing here is
  // final) but staged rather than stacked into one paragraph: a user who has
  // spun twice is skimming, and a four-sentence block is what they skim past.
  // The button carries the request route, so the text never has to describe it.
  const nudge = showNudge ? (
    <Paper
      p="sm"
      radius="md"
      bg="orange.0"
      style={{ border: '1px solid var(--mantine-color-orange-2)' }}
    >
      <Group gap="sm" wrap="nowrap" align="flex-start">
        <ThemeIcon size={26} radius="xl" color="orange" variant="light" style={{ flexShrink: 0 }}>
          <IconBulb size={15} />
        </ThemeIcon>
        <Stack gap={8} miw={0} style={{ flex: 1 }}>
          <Text size="sm" fw={600}>
            Not seeing the right meals?
          </Text>
          <Text size="sm">
            You don't have to pick them all. We'll fill the rest of the week for you.
          </Text>
          <Button
            size="xs"
            variant="light"
            color="orange"
            rightSection={<IconArrowRight size={14} />}
            onClick={handleNudgeContinue}
            style={{ alignSelf: 'flex-start' }}
          >
            Tell us what you're after
          </Button>
          <Text size="xs" c="dimmed">
            You can swap any meal once the plan is built.
          </Text>
        </Stack>
      </Group>
    </Paper>
  ) : null;

  const sections: PickerSection[] = candidates
    ? [
        {
          key: 'favorites' as const,
          title: 'Your favorites',
          icon: <IconHeart size={16} />,
          recipes: candidates.favorites,
          isCatalog: false,
        },
        {
          key: 'cookbook' as const,
          title: 'Your cookbook',
          icon: <IconBookmark size={16} />,
          recipes: [...candidates.cookbook, ...browseExtras],
          isCatalog: false,
          headerAction: hasMoreCookbook ? (
            <Button
              size="xs"
              variant="subtle"
              color="orange"
              leftSection={<IconBook size={14} />}
              onClick={handleOpenBrowse}
            >
              Browse all
            </Button>
          ) : undefined,
        },
        {
          key: 'newForYou' as const,
          title: 'New for you',
          icon: <IconSparkles size={16} />,
          recipes: candidates.newForYou,
          isCatalog: true,
          notice: nudge,
          // Only this section is server-filtered against ALL profile
          // restrictions, so the reassurance is scoped here (the other
          // sections are the user's own saved recipes)
          subtitle:
            userDietaryRestrictions.length > 0 ? (
              <Group gap={6} wrap="nowrap" align="flex-start">
                {/* Top-aligned with a nudge so wrapped text keeps the icon
                    centered on the FIRST line: (sm line-height 21.7px - 14px
                    icon) / 2 ≈ 4px */}
                <IconCheck
                  size={14}
                  color="var(--mantine-color-green-6)"
                  style={{ flexShrink: 0, marginTop: 4 }}
                />
                <Text size="sm" c="dimmed">
                  {bySegment(householdSegment, {
                    family: "Everything here fits your family's dietary needs",
                    household: "Everything here fits your household's dietary needs",
                    solo: 'Everything here fits your dietary needs',
                  })}
                </Text>
              </Group>
            ) : undefined,
          headerAction: hasMoreNewForYou ? (
            <Button
              size="xs"
              variant="subtle"
              color="orange"
              leftSection={<IconRefresh size={14} />}
              onClick={() => handleSpinAgain('header')}
              loading={refreshing}
            >
              Fresh ideas
            </Button>
          ) : undefined,
          // Same action, same label: this is the one users actually reach,
          // having scrolled the whole section, so it reads as an answer to
          // "is that all of them?" rather than as a second control. Carries
          // more visual weight than the header's subtle button because at the
          // end of a list it IS the call to action.
          footerAction: hasMoreNewForYou ? (
            <Group justify="center" mt="xs">
              <Button
                variant="light"
                color="orange"
                leftSection={<IconRefresh size={16} />}
                onClick={() => handleSpinAgain('footer')}
                loading={refreshing}
              >
                Fresh ideas
              </Button>
            </Group>
          ) : undefined,
        },
      ].filter((section) => section.recipes.length > 0)
    : [];

  const isPicked = (recipe: Recipe, isCatalog: boolean) =>
    isCatalog
      ? value.lockedCatalogRecipeIds.includes(recipe.id)
      : value.lockedRecipeIds.includes(recipe.id);

  const togglePick = (recipe: Recipe, section: PickerSection) => {
    // Mid-spin picks would race the merge (the handler pins picks as of call
    // time), so the section is inert while a refresh is in flight
    if (section.key === 'newForYou' && refreshing) return;
    const picked = isPicked(recipe, section.isCatalog);
    if (!picked && atCapacity) return;
    const listKey = section.isCatalog ? 'lockedCatalogRecipeIds' : 'lockedRecipeIds';
    const list = value[listKey];
    onChange({
      ...value,
      [listKey]: picked ? list.filter((id) => id !== recipe.id) : [...list, recipe.id],
    });
    posthog?.capture(
      picked ? AnalyticsEvent.PickerRecipeUnlocked : AnalyticsEvent.PickerRecipeLocked,
      {
        section: section.key,
        recipe_id: recipe.id,
        picked_count: pickedCount + (picked ? -1 : 1),
        // Which retrieval pool served this card — lets lock-rate be compared
        // across base/exploration/prep/trending to tune exploration slots
        ...(section.key === 'newForYou'
          ? { retrieval_source: candidates?.newForYouSources?.[recipe.id] ?? 'unknown' }
          : {}),
      }
    );
  };

  // Continue doubles as the zero-pick escape hatch: no picks = surprise-me
  const handleContinue = () => {
    if (pickedCount === 0) {
      posthog?.capture(AnalyticsEvent.PickerSkipped, { picked_count: 0 });
    }
    onNext();
  };

  return (
    <Stack gap="xl" w={{ base: '100%', sm: 700 }} mx="auto" px={{ base: 'sm', sm: 'md' }}>
      {/* Header */}
      <Stack gap="md" ta="center" align="center">
        <ThemeIcon size={80} radius="xl" variant="gradient" gradient={{ from: 'orange', to: 'red' }}>
          <IconListCheck size={40} />
        </ThemeIcon>
        <Stack gap="xs" align="center">
          {/* Deliberately a question, not an instruction: "Pick the meals you
              want this week" read as "pick all of them", and first-timers skip
              the mode step (where "we'll fill in the rest" is promised), so
              this header is the only place they can learn picking is optional */}
          <Title order={2} size="h1" fw={700} c="dark">
            Any of these look good?
          </Title>
          <Text size="lg" c="dimmed" ta="center" maw={440}>
            {isCoarsePointer ? 'Tap' : 'Click'} a few and we'll build the rest of the week
            around them. Skip it and we'll pick all {mealsNeeded} for you.
          </Text>
        </Stack>
      </Stack>

      {/* Loading skeleton: mirrors the section layout (header row + card
          grid, image in a bordered card with text below), one per section
          (favorites, cookbook, new for you) */}
      {loading &&
        [0, 1, 2].map((sectionIndex) => (
          <Stack key={sectionIndex} gap="sm">
            <Group gap="xs">
              <Skeleton height={26} width={26} radius="md" />
              <Skeleton height={14} width={120} radius="sm" />
            </Group>
            <SimpleGrid cols={{ base: 2, sm: 3 }} spacing="md">
              {Array.from({ length: 3 }).map((_, i) => (
                <Paper
                  key={i}
                  radius="lg"
                  shadow="xs"
                  style={{
                    overflow: 'hidden',
                    border: '1px solid var(--mantine-color-gray-2)',
                  }}
                >
                  <AspectRatio ratio={16 / 10}>
                    <Skeleton radius={0} />
                  </AspectRatio>
                  <Stack gap={6} p="sm">
                    <Skeleton height={12} width="85%" radius="sm" />
                    <Skeleton height={10} width="55%" radius="sm" />
                  </Stack>
                </Paper>
              ))}
            </SimpleGrid>
          </Stack>
        ))}

      {/* Load failure: don't strand the user — they can continue without picks */}
      {!loading && (loadError || sections.length === 0) && (
        <Paper p="lg" radius="lg" bg="gray.0">
          <Text size="sm" c="dimmed" ta="center">
            We couldn't put together a shortlist right now. Continue on and we'll
            pick every meal for you.
          </Text>
        </Paper>
      )}

      {/* Sections */}
      {!loading &&
        sections.map((section) => (
          <Stack key={section.key} gap="sm">
            <Group gap="xs" justify="space-between" wrap="nowrap">
              <Group gap="xs" wrap="nowrap" miw={0} style={{ flex: 1 }}>
                <ThemeIcon
                  size={26}
                  radius="md"
                  color="orange"
                  variant="light"
                  style={{ flexShrink: 0 }}
                >
                  {section.icon}
                </ThemeIcon>
                <Text fw={600} size="md" truncate>
                  {section.title}
                </Text>
              </Group>
              {/* Never let the action shrink or get clipped on narrow screens */}
              <Box style={{ flexShrink: 0 }}>{section.headerAction}</Box>
            </Group>
            {section.subtitle}
            {section.notice}
            <SimpleGrid
              cols={{ base: 2, sm: 3 }}
              spacing={{ base: 'xs', sm: 'md' }}
              style={
                section.key === 'newForYou' && refreshing
                  ? { opacity: 0.45, pointerEvents: 'none', transition: 'opacity 120ms ease' }
                  : { transition: 'opacity 120ms ease' }
              }
            >
              {section.recipes.map((recipe) => {
                const picked = isPicked(recipe, section.isCatalog);
                const blocked = !picked && atCapacity;
                const meatlessLabel = getMeatlessLabel(recipe, {
                  dietaryRestrictions: userDietaryRestrictions,
                });
                // Trending is catalog-only: in the cookbook sections the id is
                // the user's copy, not the catalog id. Those sections are the
                // user's own saved recipes, which is why nothing here passes
                // isCookbook — the section heading already says it.
                const mark = recipeMark({
                  isTrending:
                    section.isCatalog && trendingCatalogIds.has(recipe.id),
                  isCurated: recipe.isCurated,
                });
                return (
                  <Paper
                    key={recipe.id}
                    radius="lg"
                    shadow="xs"
                    onClick={() => togglePick(recipe, section)}
                    style={{
                      cursor: blocked ? 'not-allowed' : 'pointer',
                      overflow: 'hidden',
                      // Constant border width; the selected ring is box-shadow
                      // so toggling never shifts layout
                      border: `1px solid ${
                        picked ? 'var(--mantine-color-orange-5)' : 'var(--mantine-color-gray-2)'
                      }`,
                      boxShadow: picked
                        ? '0 0 0 1px var(--mantine-color-orange-5), var(--mantine-shadow-md)'
                        : undefined,
                      opacity: blocked ? 0.5 : 1,
                      transition: 'border-color 120ms ease, box-shadow 120ms ease, opacity 120ms ease',
                    }}
                  >
                    <Box pos="relative">
                      <AspectRatio ratio={16 / 10}>
                        <RecipeImage
                          src={recipe.imageUrl}
                          alt={recipe.name}
                          width="100%"
                          height="100%"
                          fallbackIconSize={32}
                        />
                      </AspectRatio>
                      {picked && (
                        <ThemeIcon
                          size={26}
                          radius="xl"
                          color="orange"
                          style={{ position: 'absolute', top: 8, right: 8 }}
                        >
                          <IconCheck size={16} />
                        </ThemeIcon>
                      )}
                      {mark && (
                        <Box
                          style={{
                            position: 'absolute',
                            bottom: 8,
                            right: 8,
                            pointerEvents: 'none',
                            // Above the nutrition scrim, which renders later
                            // in the DOM (the corner ribbon carried the same)
                            zIndex: 1,
                          }}
                        >
                          {mark === 'trending' ? <TrendingBadge /> : <TopPickBadge />}
                        </Box>
                      )}
                      {recipe.nutrition?.perServing && (
                        <>
                          {/* Gradient scrim gives the glyph contrast on any photo */}
                          <Box
                            style={{
                              position: 'absolute',
                              bottom: 0,
                              left: 0,
                              right: 0,
                              height: 44,
                              background:
                                'linear-gradient(to top, rgba(0, 0, 0, 0.45), transparent)',
                              pointerEvents: 'none',
                            }}
                          />
                          {/* Wrapper Box stops the click from toggling the pick.
                              Bottom-LEFT: the Top pick badge owns the
                              bottom-right corner on curated cards */}
                          <Box
                            onClick={(e) => e.stopPropagation()}
                            style={{ position: 'absolute', bottom: 4, left: 4 }}
                          >
                            <ActionIcon
                              size="md"
                              radius="xl"
                              variant="transparent"
                              c="white"
                              aria-label="Nutrition facts"
                              style={{ backgroundColor: 'rgba(0, 0, 0, 0.4)' }}
                              onClick={() => openNutrition(recipe, section.isCatalog)}
                            >
                              <IconChartBar size={17} stroke={1.8} />
                            </ActionIcon>
                          </Box>
                        </>
                      )}
                    </Box>
                    <Stack gap={4} p="sm">
                      {/* Three lines + a smaller font on phones: long
                          descriptive names get severely ellipsized on
                          narrow 2-col mobile cards */}
                      <Text fz={{ base: 'xs', sm: 'sm' }} fw={600} lineClamp={3}>
                        {recipe.name}
                      </Text>
                      <Group gap={6} wrap="nowrap" align="flex-start">
                        {/* Wrapping text: keep the icon centered on the
                            first line ((xs line-height 18.6px - 13px) / 2) */}
                        <IconClock
                          size={13}
                          color="var(--mantine-color-gray-6)"
                          style={{ flexShrink: 0, marginTop: 3 }}
                        />
                        <Text size="xs" c="dimmed">
                          {formatTime(recipe.totalTimeMinutes)}
                          {recipe.cuisine ? ` · ${recipe.cuisine}` : ''}
                        </Text>
                      </Group>
                      {/* Dietary signal lives here, not over the photo: the
                          image already carries the checkmark, nutrition button,
                          and Top pick badge. As readable text it also beats the
                          old leaf-only overlay for clarity. */}
                      {/* cursor inherit: Badge sets cursor: default, which
                          would break the card's pointer (and not-allowed
                          at capacity) when hovering the badge itself */}
                      {meatlessLabel && (
                        <Badge
                          size="sm"
                          variant="light"
                          color="green"
                          leftSection={<IconLeaf size={11} />}
                          style={{ alignSelf: 'flex-start', textTransform: 'none', cursor: 'inherit' }}
                        >
                          {meatlessLabel}
                        </Badge>
                      )}
                      {/* Only meaningful when the plan batch-cooks; on a
                          no-prep plan the flag would be noise on every
                          catalog card it happens to be set on */}
                      {mealPrepEnabled && recipe.mealPrepFriendly && (
                        <Badge
                          size="sm"
                          variant="light"
                          color="blue"
                          leftSection={<IconChefHat size={11} />}
                          style={{ alignSelf: 'flex-start', textTransform: 'none', cursor: 'inherit' }}
                        >
                          Preps well
                        </Badge>
                      )}
                    </Stack>
                  </Paper>
                );
              })}
            </SimpleGrid>
            {section.footerAction}
          </Stack>
        ))}

      {/* A spin can empty the section out (nothing fresh left, nothing picked
          to hold a slot), which drops it from the list along with its notice.
          Keep the nudge on screen when that happens: a user two spins deep
          into an exhausted catalog is exactly who it was written for. */}
      {!loading && !sections.some((section) => section.key === 'newForYou') && nudge}

      {/* Picked counter. Rendered at zero picks too: this is where users look
          for progress, so it's where "you don't have to pick anything" has to
          be said. Hiding it until the first pick meant the reassurance only
          ever reached users who no longer needed it. */}
      {!loading && !loadError && sections.length > 0 && (
        <Paper
          p="md"
          radius="lg"
          bg="gray.0"
          style={{ border: '1px solid var(--mantine-color-gray-2)' }}
        >
          <Group gap="sm" justify="center" wrap="nowrap">
            <ThemeIcon
              size={28}
              radius="xl"
              color={pickedCount > 0 ? 'green' : 'orange'}
              variant="light"
              style={{ flexShrink: 0 }}
            >
              {pickedCount > 0 ? <IconCheck size={16} /> : <IconWand size={16} />}
            </ThemeIcon>
            <Text size="sm" fw={500}>
              {pickedCount === 0 &&
                `Pick as many as you like, or none at all. We'll choose all ${mealsNeeded} meals for you.`}
              {pickedCount > 0 &&
                pickedCount === mealsNeeded &&
                `All ${mealsNeeded} meals picked. The whole week is yours.`}
              {pickedCount > 0 &&
                pickedCount !== mealsNeeded &&
                `${pickedCount} of ${mealsNeeded} meals picked. We'll fill the other ${
                  mealsNeeded - pickedCount
                } for you.`}
            </Text>
          </Group>
        </Paper>
      )}

      {/* Shared full nutrition facts drawer (same one the plan cards use) */}
      {nutritionRecipe?.recipe.nutrition && (
        <NutritionDrawer
          opened={nutritionDrawerOpened}
          onClose={() => setNutritionDrawerOpened(false)}
          nutrition={nutritionRecipe.recipe.nutrition}
          recipeName={nutritionRecipe.recipe.name}
          recipeId={nutritionRecipe.recipe.id}
          isOwner={!nutritionRecipe.isCatalog}
          ingredientCount={nutritionRecipe.recipe.ingredients?.length}
        />
      )}

      <RecipeSelectionDrawer
        opened={browseOpened}
        onClose={() => setBrowseOpened(false)}
        onSelectRecipe={toggleBrowsePick}
        multiSelect
        selectedRecipeIds={value.lockedRecipeIds}
        selectedDay=""
        userRecipes={fullCookbook ?? []}
        loading={cookbookLoading}
        userDietaryRestrictions={userDietaryRestrictions}
        userHouseholdSegment={householdSegment}
      />

      {/* Navigation */}
      <Group justify="space-between" pt="md" wrap="nowrap" className="wizard-footer">
        <Button
          variant="subtle"
          size="md"
          leftSection={<IconArrowLeft size={18} />}
          onClick={onBack}
          color="orange"
        >
          Back
        </Button>
        <Button
          size="md"
          leftSection={pickedCount === 0 ? <IconWand size={18} /> : undefined}
          rightSection={<IconArrowRight size={18} />}
          onClick={handleContinue}
          color="orange"
        >
          {pickedCount === 0 ? 'Surprise me' : 'Continue'}
        </Button>
      </Group>
    </Stack>
  );
};
